import React, { useState, useEffect } from "react";
import Iconify from "../../components/Iconify";
import { Button, Avatar, Tooltip } from "@mui/material";
import CustomTable from "../../components/customTable/CustomTable";
import { useSnackbar } from "notistack";
import { useAdminContext } from "../../Hooks/AdminContext";
import StatusChip from "../../theme/chip";
import DeleteConfirmation from "../../components/DeleteConfirmation";
import { useNavigate } from "react-router-dom";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import CustomCircularProgress from "../../components/CustomCircularProgress";
import {
  _Business_Center_List,
  Delete_Business_Center,
} from "../../DAL/Listing/BusinessCenter";
import { picBaseUrl } from "../../config/config";

const BusinessCenter = () => {
  const { setnavbarTitle } = useAdminContext();
  const { enqueueSnackbar } = useSnackbar();
  const [rowDataID, setRowDataID] = useState("");
  const [rowData, setRowData] = useState({});
  const [openDelete, setOpenDelete] = useState(false);
  const [loading, setLoading] = useState(true);
  const [businessCenterList, setBusinessCenterList] = useState([]);
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [pageCount, setPageCount] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Navigate to the "Add/Update Business Center" page
  const handleOpenModal = () => {
    navigate(`/business-center/add-business`);
  };

  // Delete selected business center and update the list
  const handleDelete = async () => {
    setIsLoading(true);
    const response = await Delete_Business_Center(rowDataID);
    if (response) {
      const updatedUserList = businessCenterList.filter(
        (user) => user._id !== rowDataID
      );
      setBusinessCenterList(updatedUserList);
      enqueueSnackbar(response.message, { variant: "success" });
      setTotalCount((prev) => prev - 1);
      setOpenDelete(false);
    } else {
      enqueueSnackbar(response.message, { variant: "error" });
    }
    setIsLoading(false);
  };

  // Set the selected row for deletion and open the delete confirmation modal
  const handleAgreeDelete = (row) => {
    console.log(rowData);
    setRowDataID(row._id);
    setRowData(row);
    setOpenDelete(true);
  };

  // Handles search submission, trims input, saves to localStorage, resets pagination, and fetches data
  const searchFunction = (e) => {
    e.preventDefault();
    setSearchText(searchText.trim());
    localStorage.setItem("searchText_Business_Center", searchText.trim());
    setPage(0);
    setPageCount(1);
    GetBusinessCenterList(0, rowsPerPage, searchText.trim());
  };

  // Updates current page and adjusts page count accordingly
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    if (newPage <= 0) {
      setPageCount(1);
    } else {
      setPageCount(newPage + 1);
    }
  };

  // Updates number of rows displayed per page and resets to first page
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Sets the page and page count manually (used for custom pagination controls)
  const handleChangePages = (event, newPage) => {
    if (newPage <= 0) {
      setPage(0);
      setPageCount(1);
    } else {
      setPage(newPage - 1);
      setPageCount(newPage);
    }
  };

  // Column definitions for the Business Center table.
  // Each object defines a column with optional custom rendering logic using `renderData`.
  const User_Table_Head = [
    {
      id: "action",
      label: "Action",
      renderData: (row) => (
        <>
          <Tooltip title="Edit" placement="top">
            <EditIcon
              onClick={() => {
                navigate(`/business-center/edit-business/${row._id}`, {
                  state: row,
                });
              }}
              style={{ fill: "#fff", marginRight: "10px", cursor: "pointer" }}
            />
          </Tooltip>
          <Tooltip title="Delete" placement="top">
            <DeleteOutlineIcon
              onClick={() => {
                handleAgreeDelete(row);
              }}
              style={{
                fill: "#ff0000",
                marginRight: "10px",
                cursor: "pointer",
              }}
            />
          </Tooltip>
        </>
      ),
    },
    {
      id: "number",
      label: "#",
      type: "number",
    },
    // {
    //   id: "avatar",
    //   label: "Image",
    //   renderData: (row) => (
    //     <Avatar
    //       alt={row.name}
    //       src={picBaseUrl + row.image || "/default-avatar.png"}
    //       sx={{ width: 40, height: 40 }}
    //     />
    //   ),
    // },
    {
      id: "name",
      label: "Name",
    },
    // {
    //   id: "email",
    //   label: "Email",
    // },
    // {
    //   id: "phone",
    //   label: "Phone Number",
    //   renderData: (row) => <span>{row.contact_number || "_ _"}</span>,
    // },

    {
      id: "status",
      label: "Status",
      renderData: (row) => (
        <StatusChip status={row.status} type="activeInactive" />
      ),
    },
  ];

  // Fetches the list of business centers with pagination and optional search text.
  // Updates the state with the fetched data and handles loading and error messages.
  const GetBusinessCenterList = async (page, rowsPerPage, searchText) => {
    setLoading(true);
    const FormData = {
      search: searchText,
    };
    const response = await _Business_Center_List(FormData, page, rowsPerPage);
    if (response.code === 200) {
      const { business_center_list, total_pages, total_count } = response;
      setBusinessCenterList(business_center_list);
      setTotalCount(total_count);
      setTotalPages(total_pages);
      setLoading(false);
    } else {
      setLoading(false);
      enqueueSnackbar(response.message, { variant: "error" });
    }
  };

  // On component mount or when page/rowsPerPage changes, fetches business center list
  // using the search text from local storage if available, or the current search text.
  useEffect(() => {
    const searcthecxt = localStorage.getItem("searchText_Business_Center");
    setnavbarTitle("Business Center");
    if (searcthecxt) {
      GetBusinessCenterList(page, rowsPerPage, searcthecxt);
    } else {
      GetBusinessCenterList(page, rowsPerPage, searchText);
    }
  }, [page, rowsPerPage]);

  if (loading) {
    return <CustomCircularProgress />;
  }
  return (
    <div className="mt-4">
      <DeleteConfirmation
        open={openDelete}
        isLoading={isLoading}
        setRowData={setRowData}
        setRowDataID={setRowDataID}
        setOpen={setOpenDelete}
        title={"Are you sure you want to delete this business center?"}
        handleAgree={handleDelete}
        handleClose={() => setOpenDelete(false)}
      />

      {/* Add New Business Center and Investigator Buttons */}
      <div className="d-flex justify-content-end mb-3 add-button">
        <Button
          variant="contained"
          startIcon={<Iconify icon="eva:plus-fill" />}
          onClick={handleOpenModal}
        >
          Create New Business Center
        </Button>
      </div>

      {/* Business Center List Table */}
      <div className="mt-3">
        <CustomTable
          data={businessCenterList}
          TABLE_HEAD={User_Table_Head}
          MENU_OPTIONS={[]}
          custom_search={{
            searchText: searchText,
            setSearchText: setSearchText,
            handleSubmit: searchFunction,
          }}
          custom_pagination={{
            total_count: totalCount,
            rows_per_page: rowsPerPage,
            page: page,
            handleChangePage: handleChangePage,
            onRowsPerPageChange: handleChangeRowsPerPage,
          }}
          pageCount={pageCount}
          totalPages={totalPages}
          pagePagination={true}
          handleChangePages={handleChangePages}
          placeHoldertext={"Search by name or email"}
        />
      </div>
    </div>
  );
};

export default BusinessCenter;
